import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericButton } from "@ui-components/Button/GenericButton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericStepper, { Step } from "@ui-components/Stepper/GenericStepper";
import { SkeletonLine, SkeletonText } from "@ui-components/Skeleton/GenericSkeleton";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import {
  createGroup,
  deleteGroup,
  fetchGroups,
  updateGroup,
} from "@redux/slices/notificationsV2/groupsSlice";
import { setNotificationsAccountTab } from "@redux/slices/notificationsV2AccountSlice";
import {
  useTagAssignments,
  useTags,
} from "settings/account/components/TagsManager/hooks";
import {
  buildTagLinkSelectOptions,
  filterTagsForNotificationLink,
} from "settings/account/components/TagsManager/tagLinkSelectOptions";
import {
  draftsToAssignmentApiPayload,
  fetchCurrentAssignmentsAndMembers,
  groupAssignmentsToDraft,
  groupMembersToDraftInputs,
  persistAssignmentChanges,
  persistMemberChanges,
} from "../requests/groupFormSaveHelpers";
import { tagAssignmentsToDraftRows, tagResourcesToDraftRows } from "../requests/tagNotificationMap";
import { addAssignments, addMembers, getGroup } from "../requests/request";
import type {
  AddMemberInput,
  DraftResourceAssignment,
  NotificationGroup,
} from "../types";
import GroupMembersSection from "../Groups/GroupMembersSection";
import GroupAssignmentsPicker from "../Groups/GroupAssignmentsPicker";

export const NOTIFICATIONS_ACCOUNT_ROUTE = "/account/notifications";
export const NOTIFICATION_GROUP_NEW_PATH = "/account/notifications/groups/new";

/**
 * Same palette as TagForm (`TagFormPage.tsx`) — `--color-semantic-bg-tag-*`
 * tokens from `themes.css`; readable in light and dark themes.
 */
const PRESET_COLORS = [
  "var(--color-semantic-bg-tag-cyan)",
  "var(--color-semantic-bg-tag-fuchsia)",
  "var(--color-semantic-bg-tag-turquoise)",
  "var(--color-semantic-bg-tag-blue)",
  "var(--color-semantic-bg-tag-green)",
  "var(--color-semantic-bg-tag-yellow)",
  "var(--color-semantic-bg-tag-red)",
];

const STEP_LABELS = [
  "Basic information",
  "Resources",
  "Recipients",
] as const;
type StepIndex = 0 | 1 | 2;

type AlertConfig = {
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "confirm" | "success" | "error" | "warning";
  hideCancel?: boolean;
  resolve: (value: boolean) => void;
};

interface Props {
  mode: "create" | "edit";
}

const GroupFormPage: React.FC<Props> = ({ mode }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const api = useAxiosAuth0();
  const { groupId: routeGroupId } = useParams<{ groupId: string }>();
  const tenantId = useAppSelector((s) => s.selection.tenantId);
  const partnerId = useAppSelector((s) => s.selection.partnerId);
  const isViewingAllCustomers =
    tenantId != null && partnerId != null && tenantId === partnerId;
  const groups = useAppSelector((s) => s.notificationsV2Groups.groups);
  const groupsRef = useRef(groups);
  groupsRef.current = groups;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Loaded group for recipients / assignments sections */
  const [liveGroup, setLiveGroup] = useState<NotificationGroup | null>(null);
  const [hydrated, setHydrated] = useState(mode === "create");
  const [loadError, setLoadError] = useState(false);
  const [alertConfig, setAlertConfig] = useState<AlertConfig | null>(null);
  const [activeStep, setActiveStep] = useState<StepIndex>(0);
  const [draftAssignments, setDraftAssignments] = useState<
    DraftResourceAssignment[]
  >([]);
  const [draftMembers, setDraftMembers] = useState<AddMemberInput[]>([]);
  /** Tags Manager tag id — mirrors resource assignments only; recipients are edited separately. */
  const [linkedTagId, setLinkedTagId] = useState<string | null>(null);
  /** Resources step: manual picker vs linked tag (wizard UI). */
  const [assignmentMode, setAssignmentMode] = useState<"manual" | "tag">(
    "manual"
  );
  const [editDraftsLoaded, setEditDraftsLoaded] = useState(mode === "create");

  const [baselineSig, setBaselineSig] = useState<string | null>(null);
  const baselineLockRef = useRef(false);
  const tagSyncSigRef = useRef<string>("");
  /** One GET /groups/:id per edit session (avoids re-fetch when Redux list updates). */
  const groupDetailFetchedIdRef = useRef<string | null>(null);
  /** One assignments+members load per group id (avoids re-fetch when liveGroup object updates). */
  const draftsLoadedForGroupIdRef = useRef<string | null>(null);
  /** User confirmed replace warning when switching to Linked tag (skip repeat on first tag pick). */
  const tagLinkReplaceAcknowledgedRef = useRef(false);
  /** True after hydrating Resources from GET /groups/:id `tagResources` (blocks empty tag-sync wipe). */
  const embeddedTagResourcesRef = useRef(false);
  /** Latest group detail from GET /groups/:id — avoids stale closure in async draft load. */
  const groupDetailRef = useRef<{
    linkedTagId: string | null;
    tagResourcesLength: number;
    membersLength: number;
  }>({ linkedTagId: null, tagResourcesLength: 0, membersLength: 0 });

  const shouldFetchTags = assignmentMode === "tag";
  const {
    tags: tagOptions,
    loading: tagsLoading,
    error: tagsLoadError,
  } = useTags({ enabled: shouldFetchTags });
  const { assignments: linkedTagAssignments, loading: linkedTagAssignmentsLoading } =
    useTagAssignments(shouldFetchTags ? linkedTagId : null);

  const tagLinkSelectOptions = useMemo(
    () =>
      buildTagLinkSelectOptions(
        filterTagsForNotificationLink(tagOptions, linkedTagId),
        { includeTenantInLabel: isViewingAllCustomers }
      ),
    [tagOptions, linkedTagId, isViewingAllCustomers]
  );

  const isEdit = mode === "edit";
  const isCreate = mode === "create";

  const applyGroupToForm = useCallback((g: NotificationGroup) => {
    setLiveGroup(g);
    setName(g.name ?? "");
    setDescription(g.description ?? "");
    setColor(g.color ?? null);
    const tagId = g.linkedTagId ?? null;
    groupDetailRef.current = {
      linkedTagId: tagId,
      tagResourcesLength: g.tagResources?.length ?? 0,
      membersLength: g.members?.length ?? 0,
    };
    setLinkedTagId(tagId);
    setAssignmentMode(tagId ? "tag" : "manual");

    if (tagId && g.tagResources?.length) {
      const da = tagResourcesToDraftRows(g.tagResources);
      if (da.length > 0) {
        embeddedTagResourcesRef.current = true;
        tagSyncSigRef.current = JSON.stringify(da);
        setDraftAssignments(da);
      }
    }

    if (g.members?.length) {
      setDraftMembers(groupMembersToDraftInputs(g.members));
    }

    setHydrated(true);
    setLoadError(false);
  }, []);

  const showConfirmationAlert = useCallback(
    (config: Omit<AlertConfig, "resolve">): Promise<boolean> => {
      return new Promise<boolean>((resolve) => {
        setAlertConfig({ ...config, resolve });
      });
    },
    []
  );

  const showAlert = useCallback(
    (config: Omit<AlertConfig, "resolve">): Promise<void> => {
      return new Promise<void>((resolve) => {
        setAlertConfig({
          ...config,
          hideCancel: true,
          resolve: () => {
            resolve();
          },
        });
      });
    },
    []
  );

  const handleLeave = useCallback(() => {
    dispatch(setNotificationsAccountTab("groups"));
    navigate(NOTIFICATIONS_ACCOUNT_ROUTE);
    if (tenantId) {
      void dispatch(fetchGroups({ api, tenantId }));
    }
  }, [api, dispatch, navigate, tenantId]);

  useEffect(() => {
    if (mode !== "edit" || !routeGroupId || !tenantId) return;

    const fromStore = groupsRef.current.find((g) => g.id === routeGroupId);
    if (fromStore) {
      applyGroupToForm(fromStore);
    }

    if (groupDetailFetchedIdRef.current === routeGroupId) return;

    let cancelled = false;
    groupDetailFetchedIdRef.current = routeGroupId;

    getGroup(api, tenantId, routeGroupId)
      .then((res) => {
        if (cancelled) return;
        applyGroupToForm(res.data.data);
      })
      .catch(() => {
        if (cancelled) return;
        groupDetailFetchedIdRef.current = null;
        if (!fromStore) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [mode, routeGroupId, tenantId, api, applyGroupToForm]);

  useEffect(() => {
    baselineLockRef.current = false;
    setBaselineSig(null);
    tagSyncSigRef.current = "";
    setEditDraftsLoaded(mode === "create");
    groupDetailFetchedIdRef.current = null;
    draftsLoadedForGroupIdRef.current = null;
    tagLinkReplaceAcknowledgedRef.current = false;
    embeddedTagResourcesRef.current = false;
    setLiveGroup(null);
    setHydrated(false);
    groupDetailRef.current = { linkedTagId: null, tagResourcesLength: 0, membersLength: 0 };
  }, [routeGroupId, mode]);

  useEffect(() => {
    if (!isEdit || !tenantId || !liveGroup?.id) return;
    if (draftsLoadedForGroupIdRef.current === liveGroup.id) return;

    const groupId = liveGroup.id;
    draftsLoadedForGroupIdRef.current = groupId;

    let cancelled = false;
    setEditDraftsLoaded(false);
    (async () => {
      try {
        const { assignments, members } = await fetchCurrentAssignmentsAndMembers(
          api,
          tenantId,
          groupId
        );
        if (cancelled) return;
        const { linkedTagId: linked, tagResourcesLength, membersLength } =
          groupDetailRef.current;
        // Linked-tag groups resolve resources from the tag — never overwrite
        // embedded `tagResources` (or tag-sync drafts) with empty assignments.
        if (!linked) {
          setDraftAssignments(groupAssignmentsToDraft(assignments));
        } else if (tagResourcesLength === 0 && assignments.length > 0) {
          setDraftAssignments(groupAssignmentsToDraft(assignments));
        }
        if (membersLength === 0) {
          setDraftMembers(groupMembersToDraftInputs(members));
        }
      } catch {
        if (!cancelled) draftsLoadedForGroupIdRef.current = null;
      } finally {
        if (!cancelled) setEditDraftsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, tenantId, liveGroup?.id, api]);

  useEffect(() => {
    if (!linkedTagId) {
      tagSyncSigRef.current = "";
      embeddedTagResourcesRef.current = false;
      return;
    }
    const da = tagAssignmentsToDraftRows(linkedTagAssignments);
    // Don't wipe embedded GET /groups/:id hydration while tag assignments are
    // still loading (useTagAssignments starts as [] before the fetch lands).
    if (da.length === 0) {
      if (embeddedTagResourcesRef.current) return;
      if (linkedTagAssignmentsLoading) return;
    }
    const sig = JSON.stringify(da);
    if (sig === tagSyncSigRef.current) return;
    tagSyncSigRef.current = sig;
    setDraftAssignments(da);
    if (da.length > 0) embeddedTagResourcesRef.current = false;
  }, [linkedTagId, linkedTagAssignments, linkedTagAssignmentsLoading]);

  useEffect(() => {
    if (!loadError) return;
    dispatch(setNotificationsAccountTab("groups"));
    navigate(NOTIFICATIONS_ACCOUNT_ROUTE, { replace: true });
  }, [loadError, dispatch, navigate]);

  const serializeForm = useCallback(() => {
    return JSON.stringify({
      name: name.trim(),
      description: description || null,
      color,
      linkedTagId,
      draftAssignments,
      draftMembers,
    });
  }, [
    name,
    description,
    color,
    linkedTagId,
    draftAssignments,
    draftMembers,
  ]);

  const serializeFormRef = useRef(serializeForm);
  serializeFormRef.current = serializeForm;

  useEffect(() => {
    if (!isEdit || !editDraftsLoaded || baselineLockRef.current) return;
    const id = window.setTimeout(() => {
      baselineLockRef.current = true;
      setBaselineSig(serializeFormRef.current());
    }, 150);
    return () => window.clearTimeout(id);
  }, [isEdit, editDraftsLoaded]);

  const isDirty = useMemo(() => {
    if (isCreate) return true;
    if (baselineSig === null) return false;
    return serializeForm() !== baselineSig;
  }, [isCreate, baselineSig, serializeForm]);

  const confirmResourceListReplace = useCallback(
    (context: "linked-tag-mode" | "pick-tag") => {
      const description =
        context === "linked-tag-mode"
          ? "If you link this group to a tag, your current resource list will be replaced with that tag's resources once you choose a tag. Recipients are not changed—you can still edit them on the Recipients step."
          : "Linking to this tag replaces the current resource list with the resources on the tag. Recipients are not changed—you can still edit them on the Recipients step.";
      return showConfirmationAlert({
        title: "Replace resource assignments?",
        description,
        confirmLabel: "Continue",
        cancelLabel: "Cancel",
        variant: "warning",
      });
    },
    [showConfirmationAlert]
  );

  const handleLinkedTagChange = async (nextId: string | null) => {
    const wouldReplaceOnTagPick =
      !!nextId &&
      nextId !== linkedTagId &&
      draftAssignments.length > 0 &&
      !tagLinkReplaceAcknowledgedRef.current;

    if (wouldReplaceOnTagPick) {
      const ok = await confirmResourceListReplace("pick-tag");
      if (!ok) return;
    }

    /* Clearing the link drops mirrored resources only; recipients stay as edited. */
    if (nextId === null && linkedTagId !== null) {
      tagSyncSigRef.current = "";
      embeddedTagResourcesRef.current = false;
      setDraftAssignments([]);
      tagLinkReplaceAcknowledgedRef.current = false;
    }

    if (nextId !== null && nextId !== linkedTagId) {
      embeddedTagResourcesRef.current = false;
      groupDetailRef.current = {
        ...groupDetailRef.current,
        tagResourcesLength: 0,
      };
    }

    setLinkedTagId(nextId);
    setAssignmentMode(nextId ? "tag" : "manual");
    if (nextId) tagLinkReplaceAcknowledgedRef.current = false;
  };

  const handleAssignmentModeChange = async (mode: "manual" | "tag") => {
    if (mode === "tag") {
      if (assignmentMode === "tag") return;

      if (draftAssignments.length > 0) {
        const ok = await confirmResourceListReplace("linked-tag-mode");
        if (!ok) return;
        tagLinkReplaceAcknowledgedRef.current = true;
      }

      setAssignmentMode("tag");
      return;
    }
    tagLinkReplaceAcknowledgedRef.current = false;
    if (linkedTagId !== null) {
      await handleLinkedTagChange(null);
    } else {
      setAssignmentMode("manual");
    }
  };

  const handleSaveAll = async () => {
    if (!tenantId) {
      setError("No tenant selected");
      return;
    }
    if (!name.trim()) {
      setError("Name is required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const metaBody = {
        name: name.trim(),
        description: description || null,
        color,
        linkedTagId: linkedTagId ?? null,
      };
      // Linked tag mode: the backend resolves resources from `linkedTagId`,
      // so we must NOT push assignments via POST/diff /groups/:id/assignments
      // (that endpoint is manual-mode only). Recipients are always persisted.
      const isLinkedMode = !!linkedTagId;
      if (isCreate) {
        const created = await dispatch(
          createGroup({ api, tenantId, body: metaBody })
        ).unwrap();
        const gid = created.id;
        if (!isLinkedMode && draftAssignments.length > 0) {
          await addAssignments(
            api,
            tenantId,
            gid,
            draftsToAssignmentApiPayload(draftAssignments)
          );
        }
        if (draftMembers.length > 0) {
          await addMembers(api, tenantId, gid, draftMembers);
        }
        await showAlert({
          title: "Saved",
          description: `"${created.name}" was created.`,
          confirmLabel: "OK",
          variant: "success",
        });
        // After confirming the success alert, send the user back to the
        // Notification Groups list (same UX as the edit flow). Previously
        // we routed to `/groups/:gid` which kept the wizard mounted in
        // edit mode after a brand-new create.
        handleLeave();
        return;
      }
      if (!liveGroup) return;
      await dispatch(
        updateGroup({
          api,
          tenantId,
          groupId: liveGroup.id,
          body: metaBody,
        })
      ).unwrap();
      const snap = await fetchCurrentAssignmentsAndMembers(
        api,
        tenantId,
        liveGroup.id
      );
      // In linked tag mode skip assignment writes entirely — the backend owns
      // the resource set via `linkedTagId`. Touching /groups/:id/assignments
      // here is both contract-forbidden (manual-mode only) and risky if the
      // backend materializes the tag resources into that endpoint. Recipients
      // are always persisted regardless of mode.
      if (!isLinkedMode) {
        await persistAssignmentChanges(
          api,
          tenantId,
          liveGroup.id,
          draftAssignments,
          snap.assignments
        );
      }
      await persistMemberChanges(
        api,
        tenantId,
        liveGroup.id,
        draftMembers,
        snap.members
      );
      await showAlert({
        title: "Saved",
        description: "Your notification group was updated.",
        confirmLabel: "OK",
        variant: "success",
      });
      await handleLeave();
    } catch {
      setError("Something went wrong");
      await showAlert({
        title: "Error",
        description: "Couldn't save. Please try again.",
        confirmLabel: "OK",
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!tenantId || !liveGroup) return;
    const confirmed = await showConfirmationAlert({
      title: `Delete "${liveGroup.name}"?`,
      description:
        "This action cannot be undone. The group and all its recipient and resource assignments will be removed.",
      confirmLabel: "Yes, delete it",
      cancelLabel: "Cancel",
      variant: "error",
    });
    if (!confirmed) return;
    setSubmitting(true);
    const deletedName = liveGroup.name;
    try {
      await dispatch(
        deleteGroup({ api, tenantId, groupId: liveGroup.id })
      ).unwrap();
      await showAlert({
        title: "Group deleted",
        description: `"${deletedName}" was removed.`,
        confirmLabel: "OK",
        variant: "success",
      });
      await handleLeave();
    } catch {
      await showAlert({
        title: "Error",
        description: "Couldn't delete the group. Please try again.",
        confirmLabel: "OK",
        variant: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const canProceedFromResources =
    assignmentMode === "tag" && !linkedTagId
      ? false
      : draftAssignments.length > 0;

  const stepperSteps: Step[] = useMemo(() => {
    return STEP_LABELS.map((title, idx): Step => {
      if (idx === activeStep) return { title, status: "active" };
      if (idx === 1 && !name.trim()) {
        return { title, status: "disabled" };
      }
      if (idx === 2 && !canProceedFromResources) {
        return { title, status: "disabled" };
      }
      if (idx < activeStep) return { title, status: "completed" };
      return { title };
    });
  }, [
    activeStep,
    liveGroup,
    canProceedFromResources,
    isEdit,
    isCreate,
    name,
  ]);

  const handleStepClick = (idx: number) => {
    if (idx === 1 && !name.trim()) return;
    if (idx === 2 && !canProceedFromResources) return;
    if (idx === 0 || idx === 1 || idx === 2) {
      setActiveStep(idx as StepIndex);
    }
  };

  if (isEdit && (!hydrated || !editDraftsLoaded)) {
    return (
      <div className="p-6 space-y-6" aria-busy="true" aria-label="Loading group">
        <div className="flex items-center justify-between">
          <SkeletonLine w={220} h={24} />
          <div className="flex gap-3">
            <SkeletonLine w={80} h={36} roundedClass="rounded-md" />
            <SkeletonLine w={110} h={36} roundedClass="rounded-md" />
          </div>
        </div>
        <GenericCard
          isCollapsible={false}
          bodyContent={
            <div className="flex flex-col items-center gap-8 py-6">
              <div className="flex gap-6 w-full max-w-md justify-center">
                <SkeletonLine w={140} h={14} />
                <SkeletonLine w={100} h={14} />
                <SkeletonLine w={100} h={14} />
              </div>
              <div className="w-full max-w-3xl space-y-6 px-4">
                <SkeletonLine w={180} h={20} />
                <SkeletonLine w="100%" h={40} roundedClass="rounded-md" />
                <SkeletonLine w="100%" h={80} roundedClass="rounded-md" />
                <div className="flex gap-4">
                  <SkeletonLine w="50%" h={40} roundedClass="rounded-md" />
                  <SkeletonLine w="50%" h={40} roundedClass="rounded-md" />
                </div>
                <SkeletonText lines={2} lineHeight={14} />
              </div>
            </div>
          }
        />
      </div>
    );
  }

  const basicStepContent = (
    <div className="space-y-10 max-w-3xl w-full py-2 md:py-8 text-left">
      <div className="space-y-3">
        <h2 className="text-xl font-semibold text-semantic-text-primary">
          {isEdit
            ? `Edit ${liveGroup?.name ?? "group"}`
            : "Create notification group"}
        </h2>
        <p className="text-sm text-semantic-text-secondary mt-1 max-w-xl">
          Bundle recipients and network resources. When an event fires on an
          assigned resource, everyone in the group gets notified.
        </p>
      </div>

      {error && (
        <div className="px-3 py-2 rounded-md text-sm bg-red-50 text-red-700 border border-red-200">
          {error}
        </div>
      )}

      <section className="space-y-6">
        <h3 className="text-base font-semibold text-semantic-text-primary">
          Basic information
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <GenericInput
            label="Name"
            value={name}
            onChange={setName}
            placeholder="e.g. On-call network team"
            required
            hasError={!!error && !name.trim()}
          />
          <GenericInput
            label="Description"
            value={description}
            onChange={setDescription}
            placeholder="Optional short description"
          />
        </div>
        <div className="mt-6 pt-2">
          <label className="block text-sm font-medium text-semantic-text-secondary mb-3">
            Color
          </label>
          <div className="flex items-center gap-3 flex-wrap justify-start">
            <button
              type="button"
              onClick={() => setColor(null)}
              className={`w-7 h-7 rounded-full border-2 ${
                color === null
                  ? "border-semantic-border-brand-secondary"
                  : "border-semantic-border-secondary"
              } bg-semantic-bg-primary flex items-center justify-center text-xs text-semantic-text-secondary`}
              aria-label="No color"
            >
              —
            </button>
            {PRESET_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                style={{ background: c }}
                className={`w-7 h-7 rounded-full border-2 ${
                  color === c
                    ? "border-semantic-border-brand-secondary"
                    : "border-transparent"
                }`}
                aria-label={`Color ${c}`}
              />
            ))}
          </div>
        </div>
      </section>

      {isEdit && liveGroup && (
        <section className="pt-6 mt-2 border-t border-semantic-border-secondary">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-sm font-semibold text-semantic-text-primary">
                Danger zone
              </h3>
              <p className="text-xs text-semantic-text-secondary mt-1">
                Delete this group and all of its recipient and resource
                assignments.
              </p>
            </div>
            <GenericButton
              label="Delete group"
              variant="danger"
              size="md"
              onClick={() => void handleDelete()}
              disabled={submitting}
            />
          </div>
        </section>
      )}
    </div>
  );

  const resourcesStepContent = (
    <GroupAssignmentsPicker
      group={null}
      draftAssignments={draftAssignments}
      onDraftAssignmentsChange={setDraftAssignments}
      readOnly={!!linkedTagId}
      assignmentMode={assignmentMode}
      onAssignmentModeChange={handleAssignmentModeChange}
      linkedTagId={linkedTagId}
      onLinkedTagChange={handleLinkedTagChange}
      tagLinkSelectOptions={tagLinkSelectOptions}
      tagsLoading={tagsLoading}
      tagsLoadError={tagsLoadError}
      linkedTagResourcesLoading={
        !!linkedTagId &&
        (linkedTagAssignmentsLoading ||
          (isEdit && !editDraftsLoaded && draftAssignments.length === 0))
      }
    />
  );

  const recipientsStepContent = (
    <GroupMembersSection
      group={null}
      draftMembers={draftMembers}
      onDraftMembersChange={setDraftMembers}
    />
  );

  const stepContent =
    activeStep === 0
      ? basicStepContent
      : activeStep === 1
      ? resourcesStepContent
      : recipientsStepContent;

  return (
    <div className="p-3 flex flex-col h-full overflow-hidden min-h-0">
      <GenericCard
        isCollapsible={false}
        className="flex-1 min-h-0 flex flex-col"
        bodyContent={
          <div className="flex flex-col h-full min-h-0">
            <div className="px-4 md:px-6 pt-3 pb-0 mb-4 md:mb-5 flex-shrink-0">
              <div className="w-full md:w-2/3 lg:w-1/2 mx-auto">
                <GenericStepper
                  steps={stepperSteps}
                  onStepClick={handleStepClick}
                  size="sm"
                  activeClassName="bg-semantic-bg-primary shadow-sm text-semantic-text-primary font-medium"
                  completedClassName="text-gray-400"
                  inactiveClassName="text-gray-400"
                  disabledClassName="text-gray-400 opacity-70"
                />
              </div>
            </div>

            <div
              className={`px-4 md:px-6 pb-4 md:pb-6 flex-1 min-h-0 overflow-y-auto custom-scrollbar ${
                activeStep !== 0 ? "pt-1" : "pt-2"
              }`}
            >
              <div
                className={
                  activeStep === 0
                    ? "min-h-full flex flex-col justify-center"
                    : ""
                }
              >
                {stepContent}
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 px-4 md:px-6 py-3 border-t border-semantic-border-secondary flex-shrink-0">
              <span className="text-xs text-semantic-text-secondary">
                Step {activeStep + 1} of {STEP_LABELS.length} ·{" "}
                {STEP_LABELS[activeStep]}
              </span>
              <div className="flex items-center gap-2">
                <GenericButton
                  label="Cancel"
                  variant="secondary"
                  size="md"
                  onClick={() => void handleLeave()}
                  disabled={submitting}
                />
                {activeStep === 1 && (
                  <GenericButton
                    label="← Previous"
                    variant="secondary"
                    size="md"
                    onClick={() => setActiveStep(0)}
                  />
                )}
                {activeStep === 2 && (
                  <GenericButton
                    label="← Previous"
                    variant="secondary"
                    size="md"
                    onClick={() => setActiveStep(1)}
                  />
                )}
                {activeStep === 0 && (
                  <GenericButton
                    label="Next: resources →"
                    variant="brand"
                    size="md"
                    onClick={() => setActiveStep(1)}
                    disabled={!name.trim()}
                  />
                )}
                {activeStep === 1 && (
                  <GenericButton
                    label="Next: recipients →"
                    variant="brand"
                    size="md"
                    onClick={() => setActiveStep(2)}
                    disabled={!canProceedFromResources}
                  />
                )}
                {activeStep === 2 && (
                  <GenericButton
                    label="Save"
                    variant="brand"
                    size="md"
                    loading={submitting}
                    disabled={
                      submitting ||
                      !name.trim() ||
                      (isEdit && !isDirty)
                    }
                    onClick={() => void handleSaveAll()}
                  />
                )}
              </div>
            </div>
          </div>
        }
      />

      {alertConfig && (
        <GenericAlert
          isOpen
          onConfirm={() => {
            alertConfig.resolve(true);
            setAlertConfig(null);
          }}
          onClose={() => {
            alertConfig.resolve(false);
            setAlertConfig(null);
          }}
          title={alertConfig.title}
          description={alertConfig.description}
          confirmLabel={alertConfig.confirmLabel}
          cancelLabel={alertConfig.cancelLabel}
          variant={alertConfig.variant}
          hideCancel={alertConfig.hideCancel}
        />
      )}
    </div>
  );
};

export default GroupFormPage;
